import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '../../lib/utils';

export interface SwitchProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'onChange' | 'role' | 'type'> {
  checked: boolean;
  onChange: (checked: boolean) => void;
  /** Libelle a droite de l'interrupteur ; sans libelle, fournir `aria-label`. */
  label?: string;
  /** 'sm' : interrupteur compact pour une ligne de reglage ; 'md' (defaut) aligne sur `Checkbox`. */
  size?: 'sm' | 'md';
}

/**
 * Interrupteur on/off pour un reglage qui s'applique immediatement (muet,
 * activation d'une option...). A la difference de `Checkbox`, il exprime un
 * ETAT courant plutot qu'un choix dans un formulaire.
 *
 * Semantique ARIA `switch` + `aria-checked` ; bouton natif, donc Espace /
 * Entree et focus visible gratuits. Couleurs via tokens (`primary`,
 * `neutral`), comme les autres controles de `forms/`.
 */
const Switch = forwardRef<HTMLButtonElement, SwitchProps>(
  ({ checked, onChange, label, size = 'md', disabled, className, id, ...props }, ref) => {
    const switchId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const isSmall = size === 'sm';

    return (
      <label
        htmlFor={switchId}
        className={cn(
          'inline-flex items-center gap-2',
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
          className
        )}
      >
        <button
          ref={ref}
          id={switchId}
          type="button"
          role="switch"
          aria-checked={checked}
          disabled={disabled}
          onClick={() => onChange(!checked)}
          className={cn(
            'relative inline-flex shrink-0 items-center rounded-full border-2 border-transparent transition-colors',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
            'disabled:cursor-not-allowed',
            isSmall ? 'h-5 w-9' : 'h-6 w-11',
            checked ? 'bg-primary-500' : 'bg-neutral-300'
          )}
          {...props}
        >
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none inline-block rounded-full bg-white shadow transition-transform',
              isSmall ? 'h-4 w-4' : 'h-5 w-5',
              checked ? (isSmall ? 'translate-x-4' : 'translate-x-5') : 'translate-x-0'
            )}
          />
        </button>
        {label && <span className="text-sm text-dark-500">{label}</span>}
      </label>
    );
  }
);

Switch.displayName = 'Switch';

export { Switch };
